import type { ProjectDefinition } from "../../../types";
import { type CellValue, type FuncionProjectSpec, conditions, excelDate, seededRandom } from "../spec";

const SHEET = "AV Ventas";
const TABLE = "VentasAV";
const HEADERS = ["Fecha", "Región", "Vendedor", "Producto", "Unidades", "Importe"];
const REGIONES = ["Norte", "Sur", "Centro"] as const;
const VENDEDORES = ["Ana López", "Luis Martínez", "Marta Sánchez", "Jorge Ramírez", "Sofía Torres"] as const;
const PRODUCTOS = ["Laptop", "Monitor", "Impresora", "Tableta"] as const;

const rnd = seededRandom(2525);
// Importe stays distinct so sorting has no ties.
const ROWS = Array.from({ length: 20 }, (_, i) => {
  const unidades = rnd.int(1, 20);
  return [
    excelDate("2026-04-01") + rnd.int(0, 60),
    rnd.pick(REGIONES),
    rnd.pick(VENDEDORES),
    rnd.pick(PRODUCTOS),
    unidades,
    unidades * 1000 + i * 37 + rnd.int(0, 9) * 100,
  ] as [number, string, string, string, number, number];
}).sort((a, b) => a[0] - b[0]);

type Row = (typeof ROWS)[number];
const IN = { region: "Norte", columna: "Unidades" };
const cols = (rows: Row[], idx: number[]) =>
  rows.map((r) => idx.map((i) => (i < 0 ? r[r.length + i] : r[i - 1])) as CellValue[]);
const top3 = [...ROWS].sort((a, b) => b[5] - a[5]).slice(0, 3);

const EXPECTED: CellValue[][][] = [
  cols(ROWS, [3, 6]),
  cols(ROWS, [6, 1, 3]),
  cols(ROWS, [1, -1]),
  [ROWS[ROWS.length - 1]],
  cols(ROWS.filter((r) => r[1] === IN.region), [3, 5, 6]),
  cols(top3, [3, 6]),
  cols(ROWS, [1, HEADERS.indexOf(IN.columna) + 1]),
];
const FNS = [
  ["CHOOSECOLS"], ["CHOOSECOLS"], ["CHOOSECOLS"], ["CHOOSEROWS"], ["CHOOSECOLS", "FILTER"],
  ["CHOOSECOLS", "CHOOSEROWS", "SORT"], ["CHOOSECOLS", "MATCH|XMATCH"],
];
const LABELS = [
  "Tarea 1 · Vendedor e importe",
  "Tarea 2 · Columnas reordenadas",
  "Tarea 3 · Primera y última columna",
  "Tarea 4 · Última venta",
  "Tarea 5 · Columnas de una región",
  "Tarea 6 · Top 3 por importe",
  "Tarea 7 · Columna elegida por nombre",
];

const ANCHORS: string[] = [];
EXPECTED.reduce((row, result) => {
  ANCHORS.push(`K${row}`);
  return row + result.length + 3;
}, 10);
const dateArea = (i: number, col: string) => ({
  range: `${col}${Number(ANCHORS[i].slice(1))}:${col}${Number(ANCHORS[i].slice(1)) + EXPECTED[i].length - 1}`,
  format: "dd/mm/yyyy",
});

export const spec: FuncionProjectSpec = {
  projectId: "funciones-elegir",
  sheets: [
    {
      name: SHEET,
      title: "Reportes a la medida",
      subtitle: "Ventas de abril y mayo — arma vistas con solo las columnas y filas que importan",
      tables: [{ name: TABLE, anchor: "A4", headers: HEADERS, rows: ROWS, formats: { Fecha: "dd/mm/yyyy", Importe: "$#,##0" } }],
      inputs: [
        { address: "K4", label: "Región", value: IN.region },
        { address: "K5", label: "Columna a mostrar", value: IN.columna },
      ],
      answers: ANCHORS.map((address, i) => ({
        address,
        label: LABELS[i],
        labelPosition: "above" as const,
        areas: i === 1 ? [dateArea(1, "L")] : i === 2 || i === 3 || i === 6 ? [dateArea(i, "K")] : undefined,
      })),
    },
  ],
  checks: Object.fromEntries(
    EXPECTED.map((expected, i) => [
      i + 1,
      { kind: "spill" as const, sheet: SHEET, address: ANCHORS[i], fns: FNS[i], expected },
    ])
  ),
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-elegir",
  title: "ELEGIRCOLS y ELEGIRFILAS: Reportes a la medida",
  subtitle: "Quedarte solo con las columnas y filas que necesitas",
  description:
    "Cada área quiere ver las mismas ventas de forma distinta: ventas solo vendedor e importe, dirección las columnas en otro orden, alguien más la última venta o el top 3. En lugar de copiar y borrar columnas, ELEGIRCOLS y ELEGIRFILAS arman cada vista con una fórmula que se actualiza sola.\n\nLos números indican la posición de la columna o fila; los negativos cuentan desde el final (-1 = la última).",
  topics: [
    "Elegir y reordenar columnas",
    "Índices negativos: contar desde el final",
    "ELEGIRFILAS para la última fila",
    "Combinar con FILTRAR y ORDENAR",
    "Columna elegida por su nombre con COINCIDIR",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Vendedor e importe",
      description: `En la hoja <strong>AV Ventas</strong>, celda <strong>${ANCHORS[0]}</strong>: muestra solo las columnas <strong>Vendedor</strong> (3.ª) e <strong>Importe</strong> (6.ª) de VentasAV.`,
      solution: [`Selecciona ${F(ANCHORS[0])}.`, `Escribe ${F("=ELEGIRCOLS(VentasAV,3,6)")}.`],
      expectedResult: "2 columnas, 20 filas",
      finalFormula: "=ELEGIRCOLS(VentasAV,3,6)",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Columnas en otro orden",
      description: `En <strong>${ANCHORS[1]}</strong>: muestra <strong>Importe</strong>, <strong>Fecha</strong> y <strong>Vendedor</strong>, en ese orden.`,
      solution: [
        `Selecciona ${F(ANCHORS[1])}.`,
        "El orden en que escribes los números es el orden de las columnas en el resultado.",
        `Escribe ${F("=ELEGIRCOLS(VentasAV,6,1,3)")}.`,
      ],
      expectedResult: "Importe | Fecha | Vendedor",
      finalFormula: "=ELEGIRCOLS(VentasAV,6,1,3)",
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Primera y última columna",
      description: `En <strong>${ANCHORS[2]}</strong>: muestra la <strong>primera</strong> y la <strong>última</strong> columna de la tabla, sin escribir cuántas columnas tiene.`,
      solution: [
        `Selecciona ${F(ANCHORS[2])}.`,
        `${F("-1")} es la última columna. Si mañana se agrega una columna al final, la fórmula la tomará sola.`,
        `Escribe ${F("=ELEGIRCOLS(VentasAV,1,-1)")}.`,
      ],
      expectedResult: "Fecha | Importe",
      finalFormula: "=ELEGIRCOLS(VentasAV,1,-1)",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: La venta más reciente",
      description: `En <strong>${ANCHORS[3]}</strong>: la tabla está ordenada por fecha. Muestra solo su <strong>última fila</strong> completa.`,
      solution: [`Selecciona ${F(ANCHORS[3])}.`, `Escribe ${F("=ELEGIRFILAS(VentasAV,-1)")}.`],
      expectedResult: "Una fila con la venta más reciente",
      finalFormula: "=ELEGIRFILAS(VentasAV,-1)",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Columnas de una región",
      description: `En <strong>${ANCHORS[4]}</strong>: muestra <strong>Vendedor</strong>, <strong>Unidades</strong> e <strong>Importe</strong> solo de las ventas de la región de <strong>K4</strong>.`,
      solution: [
        `Selecciona ${F(ANCHORS[4])}.`,
        "Primero FILTRAR deja las filas; luego ELEGIRCOLS deja las columnas.",
        `Escribe ${F("=ELEGIRCOLS(FILTRAR(VentasAV,VentasAV[Región]=K4),3,5,6)")}.`,
      ],
      expectedResult: "Ventas del Norte en 3 columnas",
      finalFormula: "=ELEGIRCOLS(FILTRAR(VentasAV,VentasAV[Región]=K4),3,5,6)",
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Top 3 por importe",
      description: conditions(`En <strong>${ANCHORS[5]}</strong>: muestra <strong>Vendedor</strong> e <strong>Importe</strong> de las <strong>3 ventas más grandes</strong>:`, [
        "Ordena la tabla por Importe (columna 6) de mayor a menor",
        "Quédate con las filas 1, 2 y 3 (ELEGIRFILAS)",
        "Quédate con las columnas 3 y 6 (ELEGIRCOLS)",
      ]),
      solution: [
        `Selecciona ${F(ANCHORS[5])}.`,
        `Escribe ${F("=ELEGIRCOLS(ELEGIRFILAS(ORDENAR(VentasAV,6,-1),1,2,3),3,6)")}.`,
      ],
      expectedResult: "3 filas × 2 columnas",
      finalFormula: "=ELEGIRCOLS(ELEGIRFILAS(ORDENAR(VentasAV,6,-1),1,2,3),3,6)",
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Columna elegida por su nombre",
      description: `En <strong>${ANCHORS[6]}</strong>: muestra la <strong>Fecha</strong> y la columna cuyo <strong>nombre</strong> está escrito en <strong>K5</strong>. Si alguien cambia K5 por \"Producto\", el reporte debe cambiar solo.`,
      solution: [
        `Selecciona ${F(ANCHORS[6])}.`,
        `${F("COINCIDIR(K5,VentasAV[#Encabezados],0)")} convierte el nombre de la columna en su número.`,
        `Escribe ${F("=ELEGIRCOLS(VentasAV,1,COINCIDIR(K5,VentasAV[#Encabezados],0))")}.`,
      ],
      expectedResult: "Fecha | Unidades",
      finalFormula: "=ELEGIRCOLS(VentasAV,1,COINCIDIR(K5,VentasAV[#Encabezados],0))",
    },
  ],
};
